import { Box, Image } from '@wix/design-system';
import { StatusAlertSmall, StatusCompleteSmall } from '@wix/wix-ui-icons-common';
import React, { useMemo } from 'react';
import { dataHooks } from '../../../../utils/datahooks';
import { useAltTextFixerContext } from '../context/alt-text-fixer-context';
const DEFAULT_PREVIEW_SIZE = 50;

/**
 * Props for the PreviewImage component
 */

/**
 * Check if a status represents a "passed" state
 */
const isPassedStatus = status => {
  return status === 'valid' || status === 'fixed' || status === 'decorative';
};

/**
 * Get the status icon component based on image status
 */
const getStatusIcon = status => {
  if (isPassedStatus(status)) {
    return /*#__PURE__*/React.createElement(StatusCompleteSmall, {
      "aria-label": "Completed"
    });
  }
  return /*#__PURE__*/React.createElement(StatusAlertSmall, {
    "aria-label": "Needs attention"
  });
};

/**
 * PreviewImage component
 *
 * Displays a thumbnail preview of an image with its alt-text status.
 * Shows a green checkmark for passed/valid images and a red alert for invalid ones.
 *
 * @example
 * ```tsx
 * <PreviewImage
 *   image={{ id: '1', url: 'media/123.jpg', status: 'valid', altText: 'Description' }}
 *   dataHook="my-preview"
 * />
 * ```
 */
export const PreviewImage = _ref => {
  let {
    image
  } = _ref;
  const {
    imageUtils,
    designProps
  } = useAltTextFixerContext();
  const imageSize = (designProps == null ? void 0 : designProps.imageSize) ?? DEFAULT_PREVIEW_SIZE;
  const resolvedUrl = useMemo(() => imageUtils.resolveImageUrl(image.url, imageSize, imageSize), [image.url, imageUtils, imageSize]);
  const isPassed = isPassedStatus(image.status);
  return /*#__PURE__*/React.createElement(Box, {
    border: "1px solid",
    borderColor: "D70",
    borderRadius: "4px",
    boxSizing: "border-box",
    dataHook: dataHooks.altTextFixerPreviewImage,
    position: "relative"
  }, /*#__PURE__*/React.createElement(Image, {
    alt: "",
    borderRadius: "3px",
    src: resolvedUrl,
    style: {
      opacity: isPassed ? 0.4 : 1
    }
  }), /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "D80",
    borderRadius: "50%",
    bottom: "5px",
    height: "12px",
    position: "absolute",
    right: "5px",
    width: "12px",
    zIndex: 1
  }), /*#__PURE__*/React.createElement(Box, {
    bottom: "2px",
    color: isPassed ? 'G10' : 'R10',
    position: "absolute",
    right: "2px",
    zIndex: 2
  }, getStatusIcon(image.status)));
};
export default PreviewImage;
//# sourceMappingURL=preview-image.js.map